import { defineStore } from "pinia";
import { ref } from "vue";
import type { ProductModel, CollectionModel } from "../types/shopify";
import { useApolloClient } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { useCollections, useCollection, collectionFragment, productFragment, useProduct } from "../composables/shopify";


interface ProductEdges {
  edges: Array<{ node: ProductModel }>;
}

interface CollectionEdges {
  edges: Array<{ node: CollectionModel }>;
}

interface CollectionWithProducts {
  products: {
    edges: Array<{ node: ProductModel }>
  }
}

const GET_MEDIA_IMAGE_BY_ID = gql`
  query MediaImageWithFocal($id: ID!) {
    node(id: $id) {
      ... on MediaImage {
        id
        image {
          url
          altText
        }
        presentation {
          asJson(format: IMAGE)
        }
      }
    }
  }
`;

export const useProductStore = defineStore("product", () => {
  // Reaktiva variabler (state)
  const products = ref<ProductModel[]>([]);
  const collections = ref<CollectionModel[]>([]);
  const collectionProductsByHandle = ref<Record<string, ProductModel[]>>({});
  const collectionsByHandle = ref<Record<string, CollectionModel>>({});
  const collectionsById = ref<Record<string, CollectionModel>>({});
  const productsByHandle = ref<Record<string, ProductModel>>({});

  async function initProductStore() {
    console.log('initProductStore');
    useCollections()
      .then(({ data }) => {
        if (data && data.value) {
          setCollections(data.value.collections);
        } else {
          console.warn('No collection data found');
        }
      })
      .catch(error => {
        console.error('Error initializing product store:', error);
      });
  }

  async function fetchAllProducts() {
    console.log('fetchAllProducts');
    try {
      for (const collection of collections.value) {

        await fetchCollectionProductsByHandle(collection.handle);

      }

    } catch (error) {
      console.error('Error fetching all products and collections:', error);
    }
  }

  async function fetchProductByHandle(handle: string): Promise<ProductModel | null> {
    const start = performance.now();
  
    if (!productsByHandle.value[handle]) {
      try {
        const { data } = await useProduct(handle);
        if (data?.value?.product) {
          setProductByHandle(handle, data.value.product);
        } else {
          console.warn(`No product data found for handle: ${handle}`);
        }
      } catch (error) {
        console.error(`Error fetching product for handle ${handle}:`, error);
      }
    }
  
    const end = performance.now();
    console.log(`fetchProductByHandle för '${handle}' tog: ${end - start} ms`);
  
    return productsByHandle.value[handle] ?? null;
  }
  


  // Actions
  function setProducts(newProducts: ProductEdges) {
    products.value = newProducts.edges.map(edge => edge.node);
  }

  function setCollections(newCollections: CollectionEdges) {
    collections.value = newCollections.edges.map(edge => edge.node);
  }

  function setCollectionByHandle(handle: string, collection: CollectionModel) {
    collectionsByHandle.value[handle] = collection;
  }

  function setProductByHandle(handle: string, product: ProductModel) {
    
    productsByHandle.value[handle] = product;
  }

  function setCollectionProductsByHandle(handle: string, collection: CollectionWithProducts) {
    collectionProductsByHandle.value[handle] = collection.products.edges.map(edge => edge.node);
  }

  function setCollectionById(id: string, collection: CollectionModel) {
    collectionsById.value[id] = collection;
  }


  const getCollectionByHandle = (handle: string): CollectionModel | undefined => {
    return collectionsByHandle.value[handle];
  };

  /**
   * Returnerar listan av produkter som hör till en specifik "handle",
   * eller `null` om den inte finns.
   */
  async function fetchCollectionProductsByHandle(handle: string): Promise<ProductModel[] | null> {
    const start = performance.now();
    if (!collectionProductsByHandle.value[handle]) {
      try {
        if (process.server) {
          // ✅ Körs på server – SSR-kontext, använd useCollection
          const { data } = await useCollection(handle);
          if (data?.value?.collection) {
            setCollectionByHandle(handle, data.value.collection);
            setCollectionProductsByHandle(handle, data.value.collection);
            await cacheProductsByHandle(data.value.collection.products.edges.map(edge => edge.node));
            const end = performance.now();
            console.log(`fetchCollectionProductsByHandle for '${handle}' took: ${end - start} ms`);
            return collectionProductsByHandle.value[handle];
          } else {
            console.warn(`No collection data found for handle: ${handle}`);
            return null;
          }
        } else {
          // ✅ Körs på klient – använd fetch-metod
          const { collection, products } = await fetchCollectionByHandle(handle);
  
          if (collection) {
            setCollectionByHandle(handle, collection);
            setCollectionProductsByHandle(handle, {
              products: { edges: products.map(p => ({ node: p })) },
            });
            await cacheProductsByHandle(products);
            const end = performance.now();
            console.log(`fetchCollectionProductsByHandle for '${handle}' took: ${end - start} ms`);
            return collectionProductsByHandle.value[handle];
          } else {
            console.warn(`No collection data found for handle: ${handle}`);
            return null;
          }
        }
      } catch (error) {
        console.error(`Error fetching collection products for handle ${handle}:`, error);
        return null;
      }
    }
    const end = performance.now();
    console.log(`fetchCollectionProductsByHandle for '${handle}' took: ${end - start} ms`);
    return collectionProductsByHandle.value[handle];
  }

  async function cacheProductsByHandle(productsToCache: ProductModel[]) {

    productsToCache.forEach(product => {
      if (product.handle) {
        setProductByHandle(product.handle, product);
      } else {
        console.warn(`Product is missing a handle and cannot be cached:`, product);
      }
    });


  }

  async function fetchCollectionByHandle(
    handle: string,
    first = 100
  ): Promise<{
    collection: CollectionModel | null;
    products: ProductModel[];
  }> {
    const query = gql`
      query getCollection($handle: String!, $first: Int!) {
        collection(handle: $handle) {
          ...collection
          products(first: $first) {
            edges {
              node {
                ...product
              }
            }
          }
        }
      }
      ${collectionFragment}
      ${productFragment}
    `;

    const { client } = useApolloClient();

    try {
      const { data } = await client.query({
        query,
        variables: {
          handle,
          first,
        },
      });

      if (data?.collection) {
        return {
          collection: data.collection,
          products: data.collection.products.edges.map((edge: any) => edge.node),
        };
      }

      return {
        collection: null,
        products: [],
      };
    } catch (error) {
      console.error(`Failed to fetch collection with handle "${handle}":`, error);
      return {
        collection: null,
        products: [],
      };
    }
  }

  /**
   * Hämtar en collection baserat på id. Om collectionen redan finns cachead i
   * collections-arrayen returneras den. Om den har en coverImage med ett value
   * kompletteras den med MediaImage-data.
   */
  async function fetchCollectionById(id: string): Promise<CollectionModel | null> {
    const { client } = useApolloClient();
  
    if (collectionsById.value[id]) {
      return collectionsById.value[id];
    }
  
    const query = gql`
      query getCollectionById($id: ID!) {
        node(id: $id) {
          ... on Collection {
            ...collection
          }
        }
      }
      ${collectionFragment}
    `;
  
    try {
      const { data } = await client.query({
        query,
        variables: { id },
      });
  
      if (!data?.node) {
        console.warn(`No collection data found for ID: ${id}`);
        return null;
      }
  
      const originalCollection = data.node as CollectionModel;
  
      const newCollection: CollectionModel = {
        ...originalCollection,
        coverImage: originalCollection.coverImage
          ? { ...originalCollection.coverImage }
          : undefined,
      };
  
      const coverImageId = newCollection.coverImage?.value;
  
      if (coverImageId) {
        try {
          const { data: mediaData } = await client.query({
            query: GET_MEDIA_IMAGE_BY_ID,
            variables: { id: coverImageId },
          });
  
          if (mediaData?.node && newCollection.coverImage) {
            newCollection.coverImage.reference = mediaData.node;
          }
        } catch (mediaError) {
          console.warn('Could not fetch media image for coverImage:', mediaError);
        }
      }
  
      setCollectionById(id, newCollection);
      return newCollection;
    } catch (error) {
      console.error(`Error fetching collection for ID ${id}:`, error);
      return null;
    }
  }

  return {
    products,
    collections,
    collectionProductsByHandle,
    collectionsByHandle,
    productsByHandle,
    initProductStore,
    fetchAllProducts,
    fetchProductByHandle,
    setProducts,
    setCollectionByHandle,
    setProductByHandle,
    setCollections,
    setCollectionProductsByHandle,
    getCollectionByHandle,
    fetchCollectionProductsByHandle,
    fetchCollectionById, // Den nya metoden
  };
});
